"use client"

import type React from "react"

import { Lock, MapPin, ShieldCheck } from "lucide-react"
import { useFeatureFlagVariantKey } from "posthog-js/react"

import { cn } from "@/lib/utils"
import type { EmailSuggestion } from "@/lib/email-typo-detection"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { ResultWeekStrip, ResultWeekStripFallback, WeekStripView } from "./result-week-strip"
import { buildWeatherWeek } from "@/lib/weather-week"
import type { WeatherData } from "@/lib/weather-utils"
import { ResultPlanCard, getResultPlanMonth } from "./result-plan-card"
import { LockedMonthsStrip, splitLockedTask } from "./locked-months-strip"
import { ResultUpgradeCard } from "./result-upgrade-card"
import { savedPlanEventProperties } from "@/lib/saved-plan-analytics"

interface ResultCarePlanDigestProps {
  grassType: string
  region?: string
  zipCode?: string
  /**
   * Week-ahead forecast FETCHED on the server, for surfaces that hold a ZIP they
   * must not send to the client. `/result/[id]` is a bearer URL, so it fetches
   * with the row's ZIP and passes only these numbers down — the payload carries
   * no coordinates, ZIP, or place name. Passed raw rather than pre-reduced so
   * the lead action can still be gated on this month's task prose below.
   * When set, this wins over the client fetch.
   */
  weekAhead?: WeatherData | null
  /** Footer location for `weekAhead`, e.g. "the Upper Midwest". */
  weekAheadLocationLabel?: string
  isHighConfidence: boolean
  /**
   * Calibration line for the embedded plan card, e.g. "Best guess for your
   * region: Tall Fescue." Passed for moderate-confidence results so a 66%
   * guess never presents with the certainty of a confirmed match. When absent
   * (high confidence) the card's own header is suppressed, because the digest
   * already titles the plan directly above it.
   */
  planHeaderOverride?: string
  /**
   * Whether the grass name is settled enough to say out loud in the ask copy.
   * Required, not derived from `isHighConfidence`: a user who corrected the ID
   * by hand has a confirmed name at ANY confidence, and inferring it here once
   * cost us that case when this card widened past high-confidence callers.
   */
  grassNameIsConfirmed: boolean
  /** The page hero already shows the OAuth pills; the embedded ask renders
      only the email lane. Forwarded to the upgrade card. */
  oauthShownInHero?: boolean
  /** Email-ask wiring, forwarded to the embedded upgrade card. */
  email: string
  emailSuggestion: EmailSuggestion | null
  isSubmitting: boolean
  timelineRedirectUrl?: string
  savedAnalysisId?: string | null
  onPrimaryCtaClick?: (method: "oauth" | "email") => void
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onEmailKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onSubmit: () => void
  onAcceptSuggestion: () => void
  onDismissSuggestion: () => void
  className?: string
}

/**
 * The single, canonical conversion unit on the anonymous grass-ID result screen.
 *
 * 2026-07-06: rebuilt to the "1a Account-first hero" layout from the Post
 * Analysis Page design (Claude Design Paywall project). One decision above the
 * fold: a glass account card leads with the plan-value checklist and stacked
 * OAuth pills; email is a quiet reveal
 * fallback. Below it, this month's lead task stays free and everything else
 * (rest of month + 12-month plan) is named in a lock line and blurred until
 * the plan is saved. Replaces the 2026-07-04 email-first ordering.
 *
 * High-confidence only. Moderate/low confidence and authed users keep their own
 * paths in `results-display.tsx`.
 */
export function ResultCarePlanDigest({
  grassType,
  region,
  zipCode,
  weekAhead,
  weekAheadLocationLabel,
  isHighConfidence,
  planHeaderOverride,
  grassNameIsConfirmed,
  oauthShownInHero,
  email,
  emailSuggestion,
  isSubmitting,
  timelineRedirectUrl,
  savedAnalysisId,
  onPrimaryCtaClick,
  onEmailChange,
  onEmailKeyPress,
  onSubmit,
  onAcceptSuggestion,
  onDismissSuggestion,
  className,
}: ResultCarePlanDigestProps) {
  // Standard email-capture impression event so this surface reads in the
  // capture dashboard. Source matches the signup source the email submit
  // reports, per the useImpressionEvent contract.
  const savedPlanContext = {
    analysisId: savedAnalysisId || undefined,
    mode: "identify" as const,
    planType: "care" as const,
    outcome: "identified",
    entryPath: "grass_id_result",
    subject: grassType,
  }
  // Experiment `result_soil_lead_v1`. Fail-safe like the offer-hierarchy flag:
  // only the literal variant "soil" turns the soil verdict on; undefined (SSR,
  // flags not yet resolved, PostHog off), "control", or a stray value all render
  // today's strip. Stamped on the gate impression so the arms read off the
  // existing capture dashboard without a new event.
  const soilFlag = useFeatureFlagVariantKey("result_soil_lead_v1")
  const soilVariant = soilFlag === "soil" ? "soil" : "control"
  // undefined = not resolved yet (SSR, flags still loading). The strip holds
  // its exposure event until this is true so no soil-arm person is ever
  // recorded as control.
  const soilFlagSettled = soilFlag !== undefined

  const impressionRef = useImpressionEvent<HTMLDivElement>(
    "grass_id_gate",
    savedPlanEventProperties(savedPlanContext, { grass_type: grassType, soil_variant: soilVariant }),
  )

  const { monthName, leadTask, restTasks } = getResultPlanMonth(grassType)

  // The week-ahead strip replaces the two generic checklist promises with the
  // forecast they were describing. Unflagged (Anthony's call, 2026-08-09).
  //
  // Two ways to get one: the server fetched it for us (`/result/[id]`, which
  // holds the row's ZIP but must not send it to a bearer-link viewer), or we
  // hold a ZIP the client can fetch for itself (the homepage flow, where the
  // user just typed it). Only a surface with neither falls back to the
  // checklist.
  //
  // Task prose gates the strip's action line — it may only name a move the
  // month's plan actually contains.
  const monthTaskTexts = [leadTask?.task, ...restTasks.map((t) => t.task)].filter(
    (t): t is string => Boolean(t),
  )
  // Reduced here rather than on the server so the action line can be gated on
  // the task list, which only this component knows. Pure and cheap.
  const weekContext = { soil: soilVariant === "soil", grassType, grassConfirmed: grassNameIsConfirmed }
  const serverWeek = weekAhead ? buildWeatherWeek(weekAhead, monthTaskTexts, weekContext) : null
  // Derived AFTER serverWeek on purpose: a malformed server payload reduces to
  // null, and gating on the raw prop instead would suppress the fallback while
  // rendering no chart — a hole in the card rather than the previous design.
  const showWeekStrip = Boolean(serverWeek) || Boolean(zipCode)

  return (
    <div
      id="care-plan-upgrade"
      ref={impressionRef}
      className={cn(
        // Green→white wash behind the glass card so the frosting reads.
        "relative w-full max-w-full min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-linear-to-b from-green-50/70 via-white to-amber-50/30 p-4 md:p-6 shadow-sm scroll-mt-24",
        className,
      )}
    >
      {/* Glass account card — the one decision. The OAuth ask leads (2026-08-07,
          Anthony's call); the value checklist and the free lead task sit under
          it. The result hero above this card already carries the plan promise,
          so the ask is not landing cold on the page, only on this card. */}
      {/* No backdrop-blur: it would only blur the parent's flat wash (invisible)
          while forcing a compositing layer on mobile. Gradient + inset highlight
          + green shadow carry the glass look. */}
      <div className="relative rounded-[20px] border border-white/70 bg-linear-to-b from-white/80 to-white/60 p-5 [box-shadow:0_12px_40px_rgba(22,163,74,0.10),inset_0_1px_0_rgba(255,255,255,0.85)]">
        {/* The ask lives in the hero when it renders pills there (Anthony,
            2026-08-30): this card then carries only the plan itself, titled
            here, and email stays reachable through the hero link and the
            sticky bar, both of which open the save modal. */}
        {oauthShownInHero ? (
          <h3 className="mb-4 text-center text-[17px] font-extrabold leading-tight tracking-tight text-gray-900">
            {grassNameIsConfirmed
              ? `Your 12-month ${grassType} plan`
              : "Your 12-month lawn plan"}
          </h3>
        ) : (
        <ResultUpgradeCard
          embedded
          savePlanOauth
          source="grass_id_gate"
          email={email}
          emailSuggestion={emailSuggestion}
          isSubmitting={isSubmitting}
          timelineRedirectUrl={timelineRedirectUrl}
          savedPlanContext={savedPlanContext}
          grassName={grassType}
          grassNameIsConfirmed={grassNameIsConfirmed}
          onPrimaryCtaClick={onPrimaryCtaClick}
          onEmailChange={onEmailChange}
          onEmailKeyPress={onEmailKeyPress}
          onSubmit={onSubmit}
          onAcceptSuggestion={onAcceptSuggestion}
          onDismissSuggestion={onDismissSuggestion}
        />
        )}

        {/* Badge chips, not sentences: the location and data-source claims are
            recognition material (playbook rule 3), so they render as chips a
            reader scans instead of prose they parse. */}
        {(zipCode || weekAheadLocationLabel || isHighConfidence || !oauthShownInHero) && (
          <div className="mt-3 mb-4 flex flex-wrap items-center justify-center gap-1.5">
            {(zipCode || weekAheadLocationLabel) && (
              <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-bold text-gray-700">
                <MapPin className="h-3 w-3 text-gray-500" strokeWidth={2.25} aria-hidden="true" />
                {zipCode ? `ZIP ${zipCode}` : weekAheadLocationLabel}
              </span>
            )}
            {isHighConfidence && (
              <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-bold text-gray-700">
                <ShieldCheck className="h-3 w-3 text-gray-500" strokeWidth={2.25} aria-hidden="true" />
                USDA-backed
              </span>
            )}
            {!oauthShownInHero && (
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-bold text-gray-700">
                Free, no card
              </span>
            )}
          </div>
        )}

        {/* The fallback stays HERE, above the task, while the strip proper moves
            below it. They are not interchangeable: the fallback is two more
            checklist bullets in the same visual list as the line above, so
            pushing it past the task card would split one list around a card.
            Only the forecast — a distinct block that delays the task — has a
            reason to move. */}
        {!showWeekStrip && (
          <ResultWeekStripFallback zipCode={zipCode} className="mb-4" />
        )}

        {/* Calibration line for a moderate-confidence result, rendered here
            rather than through ResultPlanCard's headerOverride: that card's
            `hideHeader` suppresses its kicker and its header line together, so
            routing the override through it would bring back the duplicate
            "Your free care plan" title under the digest's own heading. Keeping
            `hideHeader` unconditional means exactly one plan title on the card,
            with the hedge sitting directly above the task it qualifies. */}
        {planHeaderOverride && (
          <p className="mb-3 text-[13px] font-semibold leading-snug text-gray-700">
            {planHeaderOverride}
          </p>
        )}

        {/* ONE concrete free task, enough to make the plan real. `hideHeader`
            suppresses this card's own "Your free care plan" kicker — the digest
            already titles the plan directly above, and `bare` only strips the
            card chrome, not the heading. */}
        {/* id restored here because `bare` strips it from ResultPlanCard itself:
            the result_plan_preview_scrolled observer targets #care-plan-preview,
            and once every anonymous result routes through this digest
            (2026-08-08) the anchor must live on this wrapper or the metric
            silently goes authed-only. */}
        <div id="care-plan-preview" className="mb-4">
          <ResultPlanCard
            bare
            hideRestTasks
            hideHeader
            grassType={grassType}
            region={region}
            showMoreMonthsLink={false}
          />
        </div>

        {/* The strip sits AFTER the lead task, not before it. The checklist line
            above promises "starting with the one below", so anything between
            that line and the task card breaks the promise it makes. Ordered this
            way the strip reads as the evidence for the task rather than a
            preamble to it: task, then the week that justifies acting on it. */}
        {serverWeek ? (
          <WeekStripView
            week={serverWeek}
            locationLabel={weekAheadLocationLabel ?? "your region"}
            className="mb-4"
          />
        ) : zipCode ? (
          <ResultWeekStrip
            zipCode={zipCode}
            taskTexts={monthTaskTexts}
            grassType={grassType}
            grassConfirmed={grassNameIsConfirmed}
            soil={weekContext.soil}
            flagSettled={soilFlagSettled}
            className="mb-4"
          />
        ) : null}

        {/* Locked-months tease below the ask: value framing without pushing
            the sign-in buttons down. The progress line attributes the one built
            month to the user's own photo, so the 11 locked ones read as work
            waiting on them rather than a paywall. */}
        <div className="mt-4">
          <p className="text-[13px] font-medium leading-snug text-green-900">
            Month 1 is already built from your photo. Save to build the other 11.
          </p>
          <div
            className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-green-100"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={12}
            aria-valuenow={1}
            aria-label="Months built"
          >
            <div className="h-full rounded-full bg-green-600" style={{ width: `${100 / 12}%` }} />
          </div>
        </div>
        {/* This month's remaining tasks as locked chips: showing the moves
            themselves beats counting them ("3 more August tasks"). Short
            titles come from the same splitter the locked rows use. */}
        {restTasks.length > 0 && (
          <div className="mt-3 mb-1.5 flex flex-wrap items-center gap-1.5">
            {restTasks.map((rest) => (
              <span
                key={rest.task}
                className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50/60 px-2.5 py-1 text-[12px] font-semibold text-green-900"
              >
                <Lock className="h-3 w-3 flex-shrink-0 text-green-700" strokeWidth={2.25} aria-hidden="true" />
                {splitLockedTask(rest.task).title}
              </span>
            ))}
          </div>
        )}
        <LockedMonthsStrip
          bare
          hideHeader
          grassKey={grassType}
          className={restTasks.length > 0 ? undefined : "mt-3"}
        />
        <p className="mt-3 text-center text-[13px] text-gray-500">
          Save your plan above to get all 12 months
        </p>
      </div>
    </div>
  )
}
